/* eslint-disable max-lines */
import { ChevronRight } from '@material-ui/icons';
import styled, { InterpolationValue, css } from 'styled-components';
import { H4 } from '../../../../../common/components/Typography/Heading/Heading.styles';
import { DisplayCard } from '../../../../../common/components/cards/DisplayCard/DisplayCard';
import {
  DisplayCardContent,
  DisplayCardContainer,
} from '../../../../../common/components/cards/DisplayCard/DisplayCard.styles';
import { ThemeColours } from '../../../../../common/styles/ThemeColours';
import {
  Breakpoint,
  FontWeight,
  FONT_STACK,
  FontSize,
} from '../../../../../common/styles/constants';
import { TelehealthActionsVariations } from '../TelehealthActions';
import {
  DescriptionIconStyled,
  ScheduleIconStyled,
  CalendarTodayIconStyled,
  DevicesOtherOutlinedStyled,
} from '../TelehealthActions.styles';

export const ActionCardChevRight = styled(ChevronRight)`
  margin-left: auto;
  transition: transform 0.3s !important;
`;

export const ActionIconWrapper = styled.div<{
  iconColour: InterpolationValue;
  iconBackgroundColour: InterpolationValue;
  variation: TelehealthActionsVariations;
}>`
  background-color: ${({ iconBackgroundColour, variation }) =>
    iconBackgroundColour && variation === 'colour-splash'
      ? iconBackgroundColour
      : ThemeColours.primary50};
  border-radius: 50px;
  display: flex;
  padding: 8px;

  ${DescriptionIconStyled}, ${ScheduleIconStyled}, ${CalendarTodayIconStyled}, ${DevicesOtherOutlinedStyled} {
    color: ${({ iconColour, variation }) =>
      iconColour && variation === 'colour-splash' ? iconColour : ThemeColours.primary600};
    display: flex;
    font-size: 16px;
    margin-bottom: 0;
  }
`;

export const ActionCard = styled(DisplayCard)<{
  variation: TelehealthActionsVariations;
  highlight?: boolean;
}>`
  margin-right: 12px;
  min-width: 180px;
  padding: 0;
  transition: transform 0.2s, box-shadow 0.3s;

  &:first-child {
    margin-left: 16px;

    ${Breakpoint.SMALL} {
      margin-left: 0;
    }
  }

  &:last-child {
    margin-right: 16px;

    ${Breakpoint.SMALL} {
      margin-right: 0;
    }
  }

  &:active {
    ${ActionCardChevRight} {
      transform: translateX(5px);
    }
  }

  ${ActionCardChevRight} {
    transform: translateX(0);
  }

  ${DisplayCardContent} {
    align-items: flex-start;
  }

  ${DisplayCardContainer} {
    padding: 16px;

    ${Breakpoint.SMALL} {
      padding: 18px;
    }
  }

  ${({ highlight }) =>
    highlight
      ? css`
          background-color: ${ThemeColours.primary400};

          ${Breakpoint.SMALL} {
            flex: 100%;

            ${ActionFooter} {
              padding-top: 0;
            }
          }

          ${ActionTitle} {
            color: ${ThemeColours.white};

            ${Breakpoint.SMALL} {
              margin-bottom: 5px;
            }
          }

          ${ActionCardContent} {
            color: ${ThemeColours.white};
          }

          ${ActionDescription} {
            margin-bottom: 0;
          }
        `
      : null}

  ${({ variation, highlight }) => {
    const verticalEnabled = variation === 'vertical' || highlight;

    if (!verticalEnabled) return null;

    const verticalStyling = css`
      margin-bottom: 12px;
      margin-right: 0;

      ${DisplayCardContent} {
        flex-direction: row;
        align-items: center;
      }

      ${ActionFooter} {
        margin-top: unset;
        width: unset;
        align-items: center;
      }

      ${ActionSubTitle} {
        margin-bottom: 0;
      }

      ${ActionCardContent} {
        padding: 0 12px;
        margin-top: 0;
        flex: 1;
      }

      ${ActionCardChevRight} {
        padding-left: 12px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    `;

    if (highlight && variation !== 'vertical') {
      return css`
        ${Breakpoint.SMALL} {
          ${verticalStyling}
        }
      `;
    } else {
      return css`
        ${verticalStyling}
        ${Breakpoint.SMALL} {
          margin-left: 0;
          margin-right: 0;
        }

        margin-left: 12px;
        margin-right: 12px;
      `;
    }
  }}
`;

export const ActionCardContent = styled.div`
  margin-top: 14px;
  position: relative;
`;

export const ActionTitle = styled(H4)<{ subTitleInc?: boolean }>`
  font-weight: ${FontWeight.SEMI_BOLD};
  margin-top: auto;

  ${({ subTitleInc }) =>
    subTitleInc
      ? css`
          margin-bottom: 0;

          ${Breakpoint.SMALL} {
            margin-bottom: 0;
          }
        `
      : null}
`;

export const ActionSubTitle = styled.p`
  ${FONT_STACK}
  color: ${ThemeColours.mono600};
  font-size: ${FontSize.EXTRA_SMALL};
  font-weight: ${FontWeight.REGULAR};
  margin-top: 0;
`;

export const ActionDescription = styled.p`
  ${FONT_STACK}
  font-size: ${FontSize.EXTRA_SMALL};
  margin-bottom: 0;
  margin-top: 0;
`;

export const ActionPriceSubTitle = styled.p`
  ${FONT_STACK}
  font-size: ${FontSize.EXTRA_SMALL};
  margin: 0;
`;

export const ActionPrice = styled(H4)`
  ${FONT_STACK}
  font-weight: ${FontWeight.BOLD};
  margin-bottom: 0;

  ${Breakpoint.MEDIUM} {
    margin-bottom: 0;
  }
`;

export const ActionFooter = styled.div`
  align-items: end;
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 12px;
  width: 100%;
`;
